/**
 * PageToolbar
 *
 * Desktop/tablet toolbar that renders page actions from the
 * PageActionRegistry as contextual buttons.
 *
 * Layout:
 *   [Primary actions as labeled buttons]
 *   [Secondary actions as icon-only buttons]
 *   [Overflow "⋯" → dropdown with remaining actions]
 *   [⌘K shortcut hint → opens CommandPalette]
 *
 * Hidden on mobile.
 * Uses the PageActionContext from the nearest PageActionProvider.
 *
 * Accessibility:
 *   - role="toolbar" with aria-label
 *   - Arrow-key navigation (left/right) across toolbar items
 *   - Home/End to jump to first/last item
 *   - 44px min touch targets on tablet
 *   - ARIA labels on all icon-only buttons
 *
 * No domain logic — pure infrastructure.
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { usePageActionContext } from './PageActionProvider';
import { usePageActions } from '../../plugins/registries/page-action-registry';
import { useDeviceContext } from '../../providers/DeviceTypeProvider';
import type { PageAction } from '../../types/page-actions';
import { Button, Tooltip, Text, HStack } from '@neuro/design-system';
import { ActionIcon } from './ActionIcon';

// ── Priority-based classification ───────────────────────────────

/** Primary actions: priority 0–19 (show as labeled buttons) */
function isPrimary(action: PageAction): boolean {
  return (action.priority ?? 50) < 20;
}

/** Secondary actions: priority 20–39 (show as icon-only buttons) */
function isSecondary(action: PageAction): boolean {
  const p = action.priority ?? 50;
  return p >= 20 && p < 40;
}

// ── Destructive confirm helper ──────────────────────────────────

async function confirmDestructive(action: PageAction): Promise<boolean> {
  return window.confirm(
    `Are you sure you want to "${action.label}"? This action cannot be undone.`,
  );
}

// ── Component ───────────────────────────────────────────────────

export interface PageToolbarProps {
  /** Additional CSS class */
  className?: string;
  /** Max primary actions to show (default 3) */
  maxPrimary?: number;
  /** Max secondary (icon) actions to show (default 4) */
  maxSecondary?: number;
  /** Callback when overflow / ⌘K is clicked (opens CommandPalette) */
  onOverflow?: () => void;
}

export function PageToolbar({
  className,
  maxPrimary = 3,
  maxSecondary = 4,
  onOverflow,
}: PageToolbarProps) {
  const context = usePageActionContext();
  const { isMobile, isTouch } = useDeviceContext();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [executing, setExecuting] = useState<string | null>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Get toolbar-placed actions for the current context
  const toolbarActions = usePageActions(context, 'toolbar');
  const overflowActions = usePageActions(context, 'overflow');

  // Memoized: merge and deduplicate
  const allActions = useMemo(() => {
    const merged = [...toolbarActions];
    for (const a of overflowActions) {
      if (!merged.find((x) => x.id === a.id)) merged.push(a);
    }
    return merged;
  }, [toolbarActions, overflowActions]);

  // Memoized: classify into tiers
  const { primary, secondary, overflow } = useMemo(() => {
    const p = allActions.filter(isPrimary).slice(0, maxPrimary);
    const s = allActions.filter(isSecondary).slice(0, maxSecondary);
    const pIds = new Set(p.map((a) => a.id));
    const sIds = new Set(s.map((a) => a.id));
    const o = allActions.filter((a) => !pIds.has(a.id) && !sIds.has(a.id));
    return { primary: p, secondary: s, overflow: o };
  }, [allActions, maxPrimary, maxSecondary]);

  // Close overflow dropdown on outside click
  useEffect(() => {
    if (!overflowOpen) return;
    function handleClick(e: MouseEvent) {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [overflowOpen]);

  // ── Toolbar keyboard navigation (arrow keys) ──────────────────
  const handleToolbarKeyDown = useCallback((e: React.KeyboardEvent) => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const focusable = Array.from(
      toolbar.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
    if (currentIndex === -1) return;

    let nextIndex: number | null = null;
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        nextIndex = currentIndex + 1 >= focusable.length ? 0 : currentIndex + 1;
        break;
      case 'ArrowLeft':
        e.preventDefault();
        nextIndex = currentIndex - 1 < 0 ? focusable.length - 1 : currentIndex - 1;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = focusable.length - 1;
        break;
    }

    if (nextIndex !== null) {
      focusable[nextIndex].focus();
    }
  }, []);

  // Execute an action (with confirmation for destructive)
  const execute = useCallback(
    async (action: PageAction) => {
      if (action.disabled) return;
      if (action.destructive) {
        const ok = await confirmDestructive(action);
        if (!ok) return;
      }
      setExecuting(action.id);
      try {
        await action.onExecute(context);
      } catch (err) {
        console.error(`[PageToolbar] Action "${action.id}" failed:`, err);
      } finally {
        setExecuting(null);
        setOverflowOpen(false);
      }
    },
    [context],
  );

  // Hidden on mobile (FAB takes over)
  if (isMobile) return null;

  // Don't render if no actions
  if (allActions.length === 0) return null;

  // Tablet gets slightly larger touch targets via class
  const touchClass = isTouch ? 'page-toolbar--touch' : '';

  return (
    <div
      ref={toolbarRef}
      className={`page-toolbar ${touchClass} ${className ?? ''}`}
      role="toolbar"
      aria-label="Page actions"
      aria-orientation="horizontal"
      onKeyDown={handleToolbarKeyDown}
    >
      <HStack gap={6} align="center">
        {/* ── Primary: labeled buttons ── */}
        {primary.map((action) => (
          <Tooltip key={action.id} content={action.description ?? action.label}>
            <Button
              variant={action.destructive ? 'destructive' : 'secondary'}
              size="sm"
              disabled={action.disabled || executing === action.id}
              onClick={() => execute(action)}
              className="page-toolbar__action page-toolbar__action--primary"
              aria-label={action.label}
              tabIndex={0}
            >
              <HStack gap={4} align="center">
                <ActionIcon icon={action.icon} size={14} />
                <span>{executing === action.id ? '…' : action.label}</span>
              </HStack>
            </Button>
          </Tooltip>
        ))}

        {/* ── Separator ── */}
        {primary.length > 0 && secondary.length > 0 && (
          <div className="page-toolbar__separator" aria-hidden="true" />
        )}

        {/* ── Secondary: icon-only ── */}
        {secondary.map((action) => (
          <Tooltip key={action.id} content={action.description ?? action.label}>
            <Button
              variant="subtle"
              size="sm"
              disabled={action.disabled || executing === action.id}
              onClick={() => execute(action)}
              className="page-toolbar__action page-toolbar__action--secondary"
              aria-label={action.label}
              tabIndex={0}
            >
              <ActionIcon icon={action.icon} size={16} />
            </Button>
          </Tooltip>
        ))}

        {/* ── Overflow dropdown ── */}
        {overflow.length > 0 && (
          <div className="page-toolbar__overflow-container" ref={overflowRef}>
            <Tooltip content="More actions">
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  if (onOverflow) {
                    onOverflow();
                  } else {
                    setOverflowOpen((v) => !v);
                  }
                }}
                className="page-toolbar__overflow-trigger"
                aria-label="More actions"
                aria-expanded={overflowOpen}
                aria-haspopup="menu"
                tabIndex={0}
              >
                <ActionIcon icon="more-horizontal" size={16} />
              </Button>
            </Tooltip>

            {overflowOpen && !onOverflow && (
              <div
                className="page-toolbar__overflow-menu"
                role="menu"
                aria-label="Overflow actions"
              >
                {overflow.map((action) => (
                  <button
                    key={action.id}
                    role="menuitem"
                    className={`page-toolbar__overflow-item ${
                      action.destructive
                        ? 'page-toolbar__overflow-item--destructive'
                        : ''
                    }`}
                    disabled={action.disabled || executing === action.id}
                    onClick={() => execute(action)}
                    tabIndex={-1}
                  >
                    <span className="page-toolbar__overflow-icon">
                      <ActionIcon icon={action.icon} size={14} />
                    </span>
                    <span className="page-toolbar__overflow-label">
                      {executing === action.id ? 'Working…' : action.label}
                    </span>
                    {action.shortcut && (
                      <span className="page-toolbar__overflow-shortcut">
                        <Text size="xs" color="muted">
                          {action.shortcut}
                        </Text>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ⌘K shortcut hint ── */}
        {allActions.length > 0 && onOverflow && (
          <Tooltip content="Command Palette (Ctrl+K)">
            <Button
              variant="subtle"
              size="sm"
              onClick={onOverflow}
              className="page-toolbar__palette-trigger"
              aria-label="Open command palette"
              tabIndex={0}
            >
              <Text size="xs" color="muted">
                ⌘K
              </Text>
            </Button>
          </Tooltip>
        )}
      </HStack>
    </div>
  );
}
